import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

export interface EmissionOnboardingDialogData {
  vesselName?: string;
  imoNumber?: string;
  typeOfShip?: string;
  segment?: string;
}

const REQUIRED_EMISSION_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'speedVref', label: 'Speed Vref (Knots)' },
  { key: 'mcrmr', label: 'MCRMR (kW)' },
  { key: 'mcrmeLim', label: 'MCRME, Lim (kW)' },
  { key: 'pme', label: 'Pme (kW)' },
  { key: 'pmeLim', label: 'Pme, Lim (kW)' },
  { key: 'ppto', label: 'Ppto (kW)' },
  { key: 'fuelTypeMe', label: 'Type of Fuel (ME)' },
];

@Component({
  selector: 'app-emissiononboarding-popup',
  templateUrl: './emissiononboarding-popup.component.html',
  styleUrls: ['./emissiononboarding-popup.component.scss'],
})
export class EmissiononboardingPopupComponent {
  emissionForm: FormGroup;
  formSubmitted = false;
  readonly requiredEmissionFields = REQUIRED_EMISSION_FIELDS;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EmissiononboardingPopupComponent>,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: EmissionOnboardingDialogData | null,
  ) {
    this.emissionForm = this.fb.group({
      speedVref: ['', Validators.required],
      mcrmr: ['', Validators.required],
      mcrmeLim: ['', Validators.required],
      pme: ['', Validators.required],
      pmeLim: ['', Validators.required],
      ppto: ['', Validators.required],
      fuelTypeMe: ['', Validators.required],
      cfme: [''],
      sfcme: [''],
      sfcmeLim: [''],
      pae: [''],
      fuelTypeAe: [''],
      cfae: [''],
      sfeae: [''],
      iceClass: [''],
      fi: [''],
      fc: [''],
      fj: [''],
      fw: [''],
      fm: [''],
      ficsr: [''],
    });

    const prefill = this.extractEmissionPrefill(this.data);
    if (prefill) {
      this.emissionForm.patchValue(prefill);
    }
  }

  closePopup(): void {
    this.dialogRef.close();
  }

  showFieldError(controlName: string): boolean {
    const control = this.emissionForm.get(controlName);
    return !!control && control.invalid && (control.touched || this.formSubmitted);
  }

  submitEmissionDetails(event?: Event): void {
    event?.preventDefault();
    console.log('Submit button clicked');
    this.submit();
  }

  submit(): void {
    this.formSubmitted = true;
    console.log('Form valid:', this.emissionForm.valid);

    if (this.emissionForm.invalid) {
      this.emissionForm.markAllAsTouched();
      const missing = this.getMissingRequiredFields();
      if (missing.length) {
        this.toastr.error(`Please complete: ${missing.join(', ')}`);
      } else {
        this.toastr.error('Please correct invalid emission fields before submitting.');
      }
      return;
    }

    const payload = this.normalizeEmissionValues();
    console.log('Payload:', payload);
    this.dialogRef.close(payload);
  }

  private getMissingRequiredFields(): string[] {
    return REQUIRED_EMISSION_FIELDS.filter(({ key }) => {
      const control = this.emissionForm.get(key);
      return control?.hasError('required');
    }).map(({ label }) => label);
  }

  private extractEmissionPrefill(
    data: EmissionOnboardingDialogData | null,
  ): Record<string, unknown> | null {
    if (!data) {
      return null;
    }

    const record = data as Record<string, unknown>;
    const nested =
      (record['emission'] as Record<string, unknown> | undefined) ??
      (record['emission_parameters'] as Record<string, unknown> | undefined);
    const source = nested ?? record;
    const patch: Record<string, unknown> = {};

    Object.keys(this.emissionForm.controls).forEach((key) => {
      const value = source[key];
      if (value !== undefined && value !== null && value !== '') {
        patch[key] = value;
      }
    });

    return Object.keys(patch).length ? patch : null;
  }

  private normalizeEmissionValues(): Record<string, unknown> {
    const raw = this.emissionForm.getRawValue() as Record<string, unknown>;
    const numericKeys = [
      'speedVref',
      'mcrmr',
      'mcrmeLim',
      'pme',
      'pmeLim',
      'ppto',
      'cfme',
      'sfcme',
      'sfcmeLim',
      'pae',
      'cfae',
      'sfeae',
      'fi',
      'fc',
      'fj',
      'fw',
      'fm',
      'ficsr',
    ];
    const factorDefaults = new Set(['fi', 'fc', 'fj', 'fw', 'fm', 'ficsr']);

    const normalized: Record<string, unknown> = { ...raw };
    numericKeys.forEach((key) => {
      const value = normalized[key];
      const isEmpty =
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '');
      if (isEmpty) {
        normalized[key] = factorDefaults.has(key) ? 1.0 : null;
        return;
      }
      const parsed = Number(value);
      normalized[key] = Number.isFinite(parsed) ? parsed : null;
    });

    ['fuelTypeMe', 'fuelTypeAe'].forEach((key) => {
      const value = normalized[key];
      const trimmed =
        value == null ? '' : String(value).trim();
      normalized[key] = trimmed || null;
    });

    const iceClass = normalized['iceClass'];
    normalized['iceClass'] =
      iceClass == null || String(iceClass).trim() === ''
        ? null
        : String(iceClass).trim();

    return normalized;
  }
}
